import {Component, ConfigOptions} from 'panel';
import queryString from 'query-string';

import {commaizeNumber} from 'common/util';

import {track, TrackEvents, TrackProperties} from '../tracking';
import template from './index.jade';
import './index.styl';

const NO_INCUBATOR = `Select Referral Partner`;
const NONE = `I do not have a referral partner`;
const FIVE_HUNDRED = `500 Startups`;
const A16Z = `a16z`;
const ACCEL = `Accel`;
const ACE_STARTUPS = `ACE Startups`;
const ANTHEMIS_GROUP = `Anthemis Group`;
const ANTLER = `Antler`;
const BALDERTON_CAPITAL = `Balderton Capital`;
const BESSEMER = `Bessemer`;
const BETTER_CAPITAL = `Better Capital`;
const CANARY = `Canary`;
const CHERRY_VENTURES = `Cherry Ventures`;
const EAST_VENTURES = `East Ventures`;
const ENTREE_CAPITAL = `Entrée Capital`;
const F6S = `F6S`;
const FIRSTMARK_CAPITAL = `FirstMark Capital`;
const FIRST_ROUND_CAPITAL = `First Round Captial`;
const FLAT6LABS = `Flat6Labs`;
const GENERAL_CATALYST = `General Catalyst`;
const GENZ = `GenZ VCs`;
const GGV = `GGV Capital`;
const GAN = `Global Accelerators Network (GAN)`;
const GREYCROFT = `Greycroft`;
const GREYLOCK = `Greylock`;
const INDEX = `Index Ventures`;
const KASZEK = `Kaszek`;
const KIMA = `Kima Ventures`;
const KLEINER_PERKINS = `Kleiner Perkins`;
const LIGHTSPEED = `Lightspeed Venture Partners`;
const LOCALGLOBE = `LocalGlobe`;
const FOUNDERS_NETWORK = `Founders Network`;
const FRESHWORKS = `Freshworks`;
const MAINSTREET = `MainStreet`;
const MASS_CHALLENGE = `MassChallenge`;
const MONASHEES = `Monashees`;
const NEA = `NEA`;
const ONDECK = `OnDeck`;
const PARTECH = `Partech`;
const PEACHSCORE = `Peachscore`;
const PRODUCT_SCHOOL = `Product School`;
const REDPOINT = `Redpoint Ventures`;
const RIGHT_SIDE_CAP = `Right Side Capital Management`;
const SAMSUNG = `Samsung C-Labs`;
const SECRET = `Secret`;
const SEEDCAMP = `Seedcamp`;
const SEEDSTARS = `Seedstars`;
const SEGMENT = `Segment`;
const SEQUOIA = `Sequoia`;
const SOSV = `SOSV`;
const STATION_F = `Station F`;
const STARTUP_BOOTCAMP = `Startupbootcamp`;
const STARTUP_GRIND = `Startup Grind`;
const STARTUP_SCHOOL = `Startup School @ Y Combinator`;
const SURGE = `Surge`;
const TFBF = `Tech For Black Founders`;
const TECHSTARS = `Techstars`;
const THE_FAMILY = `The Family`;
const TIGER_GLOBAL = `Tiger Global`;
const VENTURE_KICK = `Venture Kick`;
const WAVEMAKER = `Wavemaker`;
const WOW_ACELERADORA = `WOW Aceleradora`;
const Y_COMBINATOR = `Y Combinator`;
const OTHER_INCUBATOR = `Other`;

const REFERRAL_INCUBATORS = [
  NO_INCUBATOR,
  NONE,
  FIVE_HUNDRED,
  A16Z,
  ACCEL,
  ACE_STARTUPS,
  ANTHEMIS_GROUP,
  ANTLER,
  BALDERTON_CAPITAL,
  BESSEMER,
  BETTER_CAPITAL,
  CANARY,
  CHERRY_VENTURES,
  EAST_VENTURES,
  ENTREE_CAPITAL,
  F6S,
  FIRSTMARK_CAPITAL,
  FIRST_ROUND_CAPITAL,
  FLAT6LABS,
  FOUNDERS_NETWORK,
  FRESHWORKS,
  GENERAL_CATALYST,
  GENZ,
  GGV,
  GAN,
  GREYCROFT,
  GREYLOCK,
  INDEX,
  KASZEK,
  KIMA,
  KLEINER_PERKINS,
  LIGHTSPEED,
  LOCALGLOBE,
  MAINSTREET,
  MASS_CHALLENGE,
  MONASHEES,
  NEA,
  ONDECK,
  PARTECH,
  PEACHSCORE,
  PRODUCT_SCHOOL,
  REDPOINT,
  RIGHT_SIDE_CAP,
  SAMSUNG,
  SECRET,
  SEEDCAMP,
  SEEDSTARS,
  SEGMENT,
  SEQUOIA,
  SOSV,
  STATION_F,
  STARTUP_BOOTCAMP,
  STARTUP_GRIND,
  STARTUP_SCHOOL,
  SURGE,
  TFBF,
  TECHSTARS,
  THE_FAMILY,
  TIGER_GLOBAL,
  VENTURE_KICK,
  WAVEMAKER,
  WOW_ACELERADORA,
  Y_COMBINATOR,
  OTHER_INCUBATOR,
];

interface StartupsApplyState {
  incubator: string;
  otherIncubator: string;
  showOther: boolean;
  eligible: boolean;
  credited: boolean;
  pastExpiry: boolean;
  loggedIn: boolean;
  org: number;
  initialValue: number;
  canUpdateBilling: boolean;
}

export class StartupsApply extends Component<StartupsApplyState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'logged-in': {type: `boolean`, description: `boolean value to indicate if user is logged in, defaults to false`},
      credited: {type: `boolean`, description: `has the user received for the credit already`},
      eligible: {type: `boolean`, description: `is this account eligible for the credit`},
      'past-expiry': {
        type: `boolean`,
        description: `if the user has the startups credit, whether or not it has expired`,
      },
      expiration: {type: `string`, description: `if the user has the startups credit, this is when they expire`},
      org: {type: `number`, description: `org id of the org selected in the org choice modal`},
      'org-options': {type: `json`, description: `array of orgs that the user belongs to`},
      'initial-value': {type: `number`, description: `initial value of the startups credit`},
      'can-update-billing': {type: `boolean`, description: `whether the user is a billing administrator or not`},
      incubator: {type: `string`, description: `the incubator referral partner for the startup applying`},
      'tech-for-black-founders': {type: `boolean`, description: `is this user referred by Tech for Black Founders?`},
      'current-org-name': {type: `string`, description: `name of the org currently being modified`},
      'mp23-brand': {type: `boolean`, description: `boolean value to indiciate if mp2023 branding items should appear`},
    };
  }
  get config(): ConfigOptions<StartupsApplyState> {
    return {
      template,
      defaultState: {
        incubator: NO_INCUBATOR,
        otherIncubator: ``,
        showOther: false,
        credited: false,
        eligible: true,
        pastExpiry: false,
        loggedIn: false,
        org: null,
        initialValue: null,
        canUpdateBilling: true,
      },
      helpers: {
        commaize: commaizeNumber,
        OTHER_INCUBATOR,
        REFERRAL_INCUBATORS,
        handleOtherInput: (ev) => {
          const otherIncubator = ev.target.value;
          this.update({otherIncubator});
        },
        continueButtonDisabled: () =>
          this.state.incubator === NO_INCUBATOR ||
          (this.state.incubator === OTHER_INCUBATOR && !this.state.otherIncubator),
        continueButtonClicked: () => {
          const next = encodeURIComponent(`/startups-apply/?incubator=${this.getIncubator()}`);
          const registerUrl = `/register/?creditStartup=1&incubator=${encodeURIComponent(
            this.getIncubator(),
          )}&next=${next}`;
          return this.state.loggedIn ? this.submitStartupsApplication() : (window.location.href = registerUrl);
        },
        getIncubator: () => this.getIncubator(),
        updateReferralIncubator: (ev) => {
          const referralIncubator = ev.detail.value;
          this.update({incubator: referralIncubator, showOther: referralIncubator === OTHER_INCUBATOR});
        },
        isTFBF: () => this.attr(`tech-for-black-founders`) || this.state.incubator === TFBF,
        getPricingUrl: () => {
          if (this.attr(`org`)) {
            return `/pricing/plan-builder/?org=${this.attr(`org`)}`;
          } else {
            return `/pricing/plan-builder`;
          }
        },
        clickPlanBuilder: (ev) => {
          ev.preventDefault();
          track(TrackEvents.StartupApplyToPlanBuilder, this.getTrackingProperties());
          window.location.href = this.helpers.getPricingUrl();
        },
        clickFreePlan: (ev) => {
          ev.preventDefault();
          track(TrackEvents.StartupApplyToFreePlan, this.getTrackingProperties());
          window.location.href = `/pricing`;
        },
        getOrgName: () => {
          return this.attr(`current-org-name`);
        },
      },
    };
  }

  getIncubator() {
    let incubator;
    switch (this.state.incubator) {
      case NO_INCUBATOR:
        incubator = `Not Provided`;
        break;
      case OTHER_INCUBATOR:
        incubator = this.state.otherIncubator || `Not Provided`;
        break;
      default:
        incubator = this.state.incubator;
        break;
    }
    return incubator;
  }

  connectedCallback() {
    super.connectedCallback();
    const update = {
      credited: this.attr(`credited`),
      eligible: this.attr(`eligible`),
      canUpdateBilling: this.attr(`can-update-billing`),
      loggedIn: this.attr(`logged-in`),
      pastExpiry: this.attr(`past-expiry`),
      org: this.attr(`org`),
      initialValue: this.attr(`initial-value`),
      incubator: this.attr(`tech-for-black-founders`) ? TFBF : NO_INCUBATOR,
    };
    const queryParams = queryString.parse(window.location.search);
    if (queryParams.incubator) {
      update.incubator = queryParams.incubator;
    }
    this.update(update);
    track(
      this.state.credited ? TrackEvents.ViewStartupsThankYou : TrackEvents.ViewStartupsApplyPage,
      this.getTrackingProperties(),
    );
  }
  submitStartupsApplication() {
    const requestForm: any = document.getElementById(`mpStartupsApply`);
    requestForm.submit();
  }
  getTrackingProperties() {
    const trackProps = {
      [TrackProperties.IsLoggedIN]: this.attr(`logged-in`),
    };
    if (!this.state.eligible && !this.state.credited) {
      if (this.state.canUpdateBilling) {
        trackProps[TrackProperties.IneligibleReason] = `paid`;
      } else {
        trackProps[TrackProperties.IneligibleReason] = `permissions`;
      }
    }
    if (this.state.credited) {
      trackProps[TrackProperties.ReferralPartner] = this.getIncubator();
    }
    return trackProps;
  }
}
customElements.define(`mp-startups-apply`, StartupsApply);
